import {
  QUALITES, TYPES_MATERIEL, TYPES_AVEC_SECOURS,
  CAPACITE_ADRESSE, CAPACITE_RESERVES,
} from '../api.js';

/* Composants de base ------------------------------------------------ */

function Champ({ intitule, aide, erreur, children }) {
  return (
    <label className="champ">
      <span className="intitule">{intitule}</span>
      {aide && <span className="aide">{aide}</span>}
      {children}
      {erreur && <span className="erreur" role="alert">{erreur}</span>}
    </label>
  );
}

function Texte({ intitule, aide, erreur, valeur, onChange, ...reste }) {
  return (
    <Champ intitule={intitule} aide={aide} erreur={erreur}>
      <input
        type="text"
        value={valeur ?? ''}
        aria-invalid={erreur ? 'true' : undefined}
        onChange={(ev) => onChange(ev.target.value)}
        {...reste}
      />
    </Champ>
  );
}

/**
 * Place restante sur le gabarit. Affichée en continu : la contrainte vient du
 * papier, pas d'un caprice logiciel, et il vaut mieux la voir en saisissant qu'au
 * moment de faire signer, client présent.
 */
function Compteur({ valeur, capacite }) {
  const utilises = (valeur ?? '').length;
  const restants = capacite - utilises;
  const alerte = restants < 0;
  const proche = !alerte && restants <= 30;
  return (
    <span
      className="compteur"
      style={{ color: alerte ? 'var(--erreur)' : proche ? '#8a6100' : 'var(--texte-doux)' }}
      aria-live="polite"
    >
      {alerte
        ? `${-restants} caractère${-restants > 1 ? 's' : ''} de trop`
        : `${restants} caractère${restants > 1 ? 's' : ''} restant${restants > 1 ? 's' : ''}`}
    </span>
  );
}

function Case({ coche, onChange, libelle }) {
  return (
    <label className={`choix${coche ? ' coche' : ''}`}>
      <input type="checkbox" checked={coche} onChange={(ev) => onChange(ev.target.checked)} />
      <span className="libelle">{libelle}</span>
    </label>
  );
}

/* Étape 1 — l'affaire ----------------------------------------------- */

export function EtapeAffaire({ f, set, err }) {
  return (
    <>
      <h2>L'affaire</h2>
      <p className="sous-titre">Ces informations identifient la réception. Elles seront reportées à l'identique sur la fiche signée.</p>
      <div className="carte">
        <Texte
          intitule="Date de réception"
          erreur={err.dateReception}
          valeur={f.dateReception}
          onChange={(v) => set({ dateReception: v })}
          inputMode="numeric"
          placeholder="JJ/MM/AAAA"
        />
        <Texte
          intitule="N° d'appareil"
          erreur={err.appareilNo}
          valeur={f.appareilNo}
          onChange={(v) => set({ appareilNo: v.toUpperCase() })}
          autoCapitalize="characters"
        />
        <Texte
          intitule="N° de devis (facultatif)"
          erreur={err.devisNo}
          valeur={f.devisNo}
          onChange={(v) => set({ devisNo: v.toUpperCase() })}
          autoCapitalize="characters"
        />
        <Texte
          intitule="Nom de l'affaire"
          erreur={err.nomAffaire}
          valeur={f.nomAffaire}
          onChange={(v) => set({ nomAffaire: v })}
        />
        <Champ
          intitule="Adresse du chantier"
          aide="Deux lignes disponibles sur la fiche."
          erreur={err.adresse}
        >
          <textarea
            value={f.adresse}
            aria-invalid={err.adresse ? 'true' : undefined}
            onChange={(ev) => set({ adresse: ev.target.value })}
          />
          <Compteur valeur={f.adresse} capacite={CAPACITE_ADRESSE} />
        </Champ>
      </div>
    </>
  );
}

/* Étape 2 — le matériel --------------------------------------------- */

export function EtapeMateriel({ f, set, err }) {
  const basculer = (cle, actif) => {
    const types = actif
      ? [...f.materiel.types, cle]
      : f.materiel.types.filter((t) => t !== cle);
    set({ materiel: { ...f.materiel, types } });
  };
  const secoursApplicable = f.materiel.types.some((t) => TYPES_AVEC_SECOURS.includes(t));

  return (
    <>
      <h2>Le matériel</h2>
      <p className="sous-titre">Caractéristiques principales, telles qu'elles figurent au bloc du même nom.</p>

      <div className="carte">
        <span className="intitule" style={{ display: 'block', fontWeight: 600, fontSize: 14, marginBottom: 8 }}>
          Type d'appareil
        </span>
        {TYPES_MATERIEL.map(([cle, libelle]) => (
          <Case
            key={cle}
            libelle={libelle}
            coche={f.materiel.types.includes(cle)}
            onChange={(actif) => basculer(cle, actif)}
          />
        ))}
        {err.types && <span className="erreur" role="alert">{err.types}</span>}

        {f.materiel.types.includes('autre') && (
          <div style={{ marginTop: 12 }}>
            <Texte
              intitule="Préciser"
              erreur={err.autrePrecision}
              valeur={f.materiel.autrePrecision}
              onChange={(v) => set({ materiel: { ...f.materiel, autrePrecision: v } })}
            />
          </div>
        )}
      </div>

      <div className="carte">
        <Texte
          intitule="Type"
          erreur={err.designation}
          valeur={f.materiel.designation}
          onChange={(v) => set({ materiel: { ...f.materiel, designation: v } })}
        />
        <Texte
          intitule="Nombre de niveaux"
          erreur={err.nbreNiveaux}
          valeur={f.materiel.nbreNiveaux}
          onChange={(v) => set({ materiel: { ...f.materiel, nbreNiveaux: v } })}
          inputMode="numeric"
        />
        <Texte
          intitule="Charge (kg)"
          erreur={err.chargeKg}
          valeur={f.materiel.chargeKg}
          onChange={(v) => set({ materiel: { ...f.materiel, chargeKg: v } })}
          inputMode="numeric"
        />
      </div>

      {secoursApplicable && (
        <div className="carte">
          <p className="sous-titre" style={{ marginBottom: 10 }}>
            Point b) de la fiche — applicable aux élévateurs PMR et ascenseurs de maison.
          </p>
          <Case
            libelle="Le dispositif de secours par téléphone est en état de fonctionnement, et il a été spécifié au client qu'il lui appartient de s'assurer en permanence de son bon fonctionnement."
            coche={f.secoursTelephoniqueVerifie === true}
            onChange={(v) => set({ secoursTelephoniqueVerifie: v })}
          />
          {err.secoursTelephoniqueVerifie && (
            <span className="erreur" role="alert">{err.secoursTelephoniqueVerifie}</span>
          )}
        </div>
      )}
    </>
  );
}

/* Étape 4 — réserves ------------------------------------------------ */

export function EtapeReserves({ f, set, err }) {
  return (
    <>
      <h2>Réception</h2>
      <p className="sous-titre">
        Le choix conditionne la suite : une réception avec réserves restera ouverte jusqu'à
        la signature de leur levée.
      </p>
      <div className="carte">
        <label className={`choix${f.avecReserves === false ? ' coche' : ''}`}>
          <input type="radio" name="reserve" checked={f.avecReserves === false} onChange={() => set({ avecReserves: false, natureReserves: '' })} />
          <span className="libelle">Sans réserve</span>
        </label>
        <label className={`choix${f.avecReserves === true ? ' coche' : ''}`}>
          <input type="radio" name="reserve" checked={f.avecReserves === true} onChange={() => set({ avecReserves: true })} />
          <span className="libelle">Avec réserves</span>
        </label>
        {err.avecReserves && <span className="erreur" role="alert">{err.avecReserves}</span>}
      </div>

      {f.avecReserves === true && (
        <div className="carte">
          <Champ
            intitule="Nature des réserves à lever"
            aide="Trois lignes disponibles sur la fiche. Au-delà, prévoir une annexe."
            erreur={err.natureReserves}
          >
            <textarea
              value={f.natureReserves}
              aria-invalid={err.natureReserves ? 'true' : undefined}
              onChange={(ev) => set({ natureReserves: ev.target.value })}
              placeholder="Décrire précisément chaque réserve, poste par poste."
            />
            <Compteur valeur={f.natureReserves} capacite={CAPACITE_RESERVES} />
          </Champ>
        </div>
      )}
    </>
  );
}

/* Étape 5 — signataires --------------------------------------------- */

export function EtapeSignataires({ f, set, err }) {
  const client = (v) => set({ signataireClient: { ...f.signataireClient, ...v } });
  const etna = (v) => set({ representantEtna: { ...f.representantEtna, ...v } });

  return (
    <>
      <h2>Les signataires</h2>
      <p className="sous-titre">
        Le numéro de mobile reçoit le code de confirmation : il doit être celui de la
        personne présente, et joignable maintenant.
      </p>

      <div className="carte">
        <span className="intitule" style={{ display: 'block', fontWeight: 600, fontSize: 14, marginBottom: 10 }}>
          Côté client
        </span>
        <Texte intitule="Nom et prénom" erreur={err.clientNom} valeur={f.signataireClient.nomComplet} onChange={(v) => client({ nomComplet: v })} />
        <Champ intitule="Agissant en qualité de" erreur={err.clientQualite}>
          <select value={f.signataireClient.qualite} onChange={(ev) => client({ qualite: ev.target.value })}>
            <option value="">— Choisir —</option>
            {QUALITES.map(([cle, libelle]) => <option key={cle} value={cle}>{libelle}</option>)}
          </select>
        </Champ>
        {['maitreOeuvre', 'representant'].includes(f.signataireClient.qualite) && (
          <>
            <Case
              libelle="J'atteste être mandaté(e) par le maître d'ouvrage à prononcer la réception."
              coche={f.signataireClient.habilitationMoa === true}
              onChange={(v) => client({ habilitationMoa: v })}
            />
            {err.clientHabilitation && <span className="erreur" role="alert">{err.clientHabilitation}</span>}
          </>
        )}
        <Champ intitule="Adresse e-mail" erreur={err.clientEmail}>
          <input type="email" inputMode="email" autoCapitalize="off" autoCorrect="off"
            value={f.signataireClient.email} aria-invalid={err.clientEmail ? 'true' : undefined}
            onChange={(ev) => client({ email: ev.target.value.trim() })} />
        </Champ>
        <Champ intitule="Mobile" aide="Ex. 0612345678 ou +33612345678" erreur={err.clientTelephone}>
          <input type="tel" inputMode="tel"
            value={f.signataireClient.telephone} aria-invalid={err.clientTelephone ? 'true' : undefined}
            onChange={(ev) => client({ telephone: ev.target.value.replace(/[^\d+]/g, '') })} />
        </Champ>
      </div>

      <div className="carte">
        <span className="intitule" style={{ display: 'block', fontWeight: 600, fontSize: 14, marginBottom: 10 }}>
          Représentant Etna France
        </span>
        <Texte intitule="Nom et prénom" erreur={err.etnaNom} valeur={f.representantEtna.nomComplet} onChange={(v) => etna({ nomComplet: v })} />
        <Champ intitule="Adresse e-mail" erreur={err.etnaEmail}>
          <input type="email" inputMode="email" autoCapitalize="off" autoCorrect="off"
            value={f.representantEtna.email} aria-invalid={err.etnaEmail ? 'true' : undefined}
            onChange={(ev) => etna({ email: ev.target.value.trim() })} />
        </Champ>
        <Champ intitule="Mobile" aide="Ex. 0612345678 ou +33612345678" erreur={err.etnaTelephone}>
          <input type="tel" inputMode="tel"
            value={f.representantEtna.telephone} aria-invalid={err.etnaTelephone ? 'true' : undefined}
            onChange={(ev) => etna({ telephone: ev.target.value.replace(/[^\d+]/g, '') })} />
        </Champ>
        <Case
          libelle="Je suis mandaté par Etna France à prononcer la réception."
          coche={f.representantEtna.habilite === true}
          onChange={(v) => etna({ habilite: v })}
        />
        {err.etnaHabilite && <span className="erreur" role="alert">{err.etnaHabilite}</span>}
      </div>
    </>
  );
}

/* Étape 6 — récapitulatif ------------------------------------------- */

export function EtapeRecap({ f }) {
  const libelleType = (cle) => TYPES_MATERIEL.find(([c]) => c === cle)?.[1] ?? cle;

  return (
    <>
      <h2>Avant signature</h2>
      <p className="sous-titre">Faire relire cet écran au client. Après signature, la fiche n'est plus modifiable.</p>
      <div className="carte recap">
        <dl>
          <dt>Affaire</dt><dd>{f.nomAffaire}</dd>
          <dt>Appareil / devis</dt><dd>{f.appareilNo}{f.devisNo ? ` — ${f.devisNo}` : ''}</dd>
          <dt>Date</dt><dd>{f.dateReception}</dd>
          <dt>Adresse</dt><dd>{f.adresse}</dd>
          <dt>Matériel</dt>
          <dd>
            {f.materiel.types.map(libelleType).join(', ')}
            {f.materiel.autrePrecision ? ` (${f.materiel.autrePrecision})` : ''}
            {' — '}{f.materiel.designation}, {f.materiel.nbreNiveaux} niveaux, {f.materiel.chargeKg} kg
          </dd>
          <dt>Réception</dt>
          <dd>{f.avecReserves ? 'Avec réserves' : 'Sans réserve'}</dd>
          {f.avecReserves && <><dt>Réserves</dt><dd>{f.natureReserves}</dd></>}
          <dt>Signataires</dt>
          <dd>
            {f.signataireClient.nomComplet} ({QUALITES.find(([c]) => c === f.signataireClient.qualite)?.[1]})
            {' · '}{f.representantEtna.nomComplet} pour Etna France
          </dd>
        </dl>
        <p className="avert">
          En signant, le client prononce la réception définitive, valant transfert de propriété
          sous réserve du paiement intégral, et reconnaît avoir pris connaissance de la clause
          de réserve de propriété figurant au verso du document présenté.
        </p>
      </div>
    </>
  );
}
